import { Location } from '@angular/common'
import { Component, HostBinding, Injectable, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { every } from 'lodash-es'
import { LazyLoadEvent } from 'primeng/api'
import { resourceUsage } from 'process'
import { BehaviorSubject, Subject, combineLatest, forkJoin } from 'rxjs'
import { debounceTime, filter, map, shareReplay, switchMap, take, tap, withLatestFrom } from 'rxjs/operators'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { AppPractice, PIMSProduct, ProductType, ProductType2, ProductsClient, VetCorProduct } from 'src/app/shared/services/ppv.api'
import { ProductsService } from '../products.service'
import { ComponentStore } from '@ngrx/component-store'
import { PIMSProductUIModel } from '../models/models'
import { CheckboxChangeEvent } from 'primeng/checkbox'

type ProductTypeEnum = {
   productTypeSKey: number
   productTypeName: string
}

@Injectable()
export class ProductsListComponentStore extends ComponentStore<{
   selectedPracticeSKey?: number
   productTypeSKey?: number
   loadingPIMS: boolean
   pimsRows?: PIMSProductUIModel[]
   onlySoldLastTTM: boolean
   onlyUnconfirmedProducts: boolean
   onlyUnmappedProducts: boolean
   onlyActiveProducts: boolean
   searchText: string
   searchCode: string
   selectedCategory: string
   selectedSite: string
}> {
   constructor() {
      super({
         loadingPIMS: false,
         onlySoldLastTTM: true,
         onlyUnconfirmedProducts: false,
         onlyUnmappedProducts: false,
         onlyActiveProducts: true,
         searchText: '',
         searchCode: '',
         selectedCategory: '',
         selectedSite: '',
      })
   }
}

@Component({
   selector: 'products-list',
   templateUrl: './products-list.component.html',
   styleUrls: ['./products-list.component.scss'],
   providers: [ProductsListComponentStore],
   standalone: false,
})
export class ProductsListComponent implements OnInit {
   loading = true
   rows: VetCorProduct[] = []
   rowsToTake = 100
   practicesLookup$ = new BehaviorSubject<{
      [practiceSKey: number]: AppPractice
   }>(undefined)
   practices: AppPractice[] = []
   searchTextChanged$ = new Subject<string>()
   searchTextInput = ''

   selectedSite = ''
   siteCount = 1
   isSelectedSite = false
   sites: any[] = []

   selectedCategory = ''
   yourCatList: any[] = []
   searchCodeInput = ''
   searchCodeChanged$ = new Subject<string>()

   loading$ = this.productsService.loading$
   productTypes: ProductTypeEnum[] = Object.values(ProductType)
      .filter((p) => typeof p === 'string')
      .map(
         (p) =>
            <ProductTypeEnum>{
               productTypeSKey: ProductType[p],
               productTypeName: p,
            }
      )
   missingPracticesVisible = false

   selectedPractice$ = combineLatest([this.store.select((s) => s.selectedPracticeSKey), this.practicesLookup$]).pipe(
      map(([selectedPracticeSKey, practiceLookup]) => (practiceLookup ? practiceLookup[selectedPracticeSKey] : undefined))
   )

   state$ = combineLatest([this.store.select((s) => s), this.selectedPractice$]).pipe(
      map(([state, selectedPractice]) => ({
         ...state,
         selectedPractice,
      }))
   )

   @HostBinding('class.full-flex-height') fullFlexHeight = true

   constructor(
      private api: ProductsClient,
      private authManager: AuthManager,
      private route: ActivatedRoute,
      private location: Location,
      private productsService: ProductsService,
      private store: ProductsListComponentStore
   ) {}

   ngOnInit(): void {
      const practiceSKey = this.route.snapshot.paramMap.get('practiceSKey')

      this.store.setState({
         selectedPracticeSKey: !!practiceSKey ? Number.parseInt(practiceSKey) : undefined,
         loadingPIMS: false,
         onlySoldLastTTM: true,
         onlyUnconfirmedProducts: false,
         onlyUnmappedProducts: false,
         onlyActiveProducts: true,
         searchText: '',
         searchCode: '',
         selectedCategory: '',
         selectedSite: '',
      })

      this.searchTextChanged$.pipe(debounceTime(300)).subscribe((searchText) => {
         this.store.patchState({
            searchText: searchText,
         })
      })

      this.searchCodeChanged$.pipe(debounceTime(300)).subscribe((searchCode) => {
         if (searchCode) {
            this.store.setState((state) => ({
               ...state,
               pimsRows: state.pimsRows?.filter((row) => row.code.toLowerCase().includes(searchCode.toLowerCase())),
            }))
         }

         this.store.patchState({
            searchCode: searchCode,
         })
      })

      this.productsService.loadVCProducts()

      this.authManager
         .getCurrentUser()
         .pipe(
            take(1),
            tap((user) => {
               this.practices = user.practices
               const newLookup = {}
               this.practices.forEach((prac) => {
                  newLookup[prac.id] = prac
               })
               this.practicesLookup$.next(newLookup)
               if (this.practices.length == 1) {
                  this.onPracticeSelected(this.practices[0])
               }
            })
         )
         .subscribe()

      const pimsProductResults$ = this.store
         .select((s) => s.selectedPracticeSKey)
         .pipe(
            tap((prac) => {
               if (!prac)
                  this.store.patchState({
                     pimsRows: undefined,
                  })
            }),
            filter((prac) => !!prac),
            tap((prac) => {
               this.store.patchState({
                  loadingPIMS: true,
               })
            }),
            switchMap((practiceSKey) =>
               this.api.searchPIMSProducts({
                  practiceSKey,
               })
            )
         )

      combineLatest([
         pimsProductResults$,
         this.practicesLookup$,
         this.store.select((s) => s.productTypeSKey),
         this.store.select((s) => s.onlySoldLastTTM),
         this.store.select((s) => s.onlyUnconfirmedProducts),
         this.store.select((s) => s.onlyUnmappedProducts),
         this.store.select((s) => s.onlyActiveProducts),
         this.store.select((s) => s.searchText),
         this.store.select((s) => s.searchCode),
         this.store.select((s) => s.selectedCategory),
         this.store.select((s) => s.selectedSite),
         this.productsService.allCategories$,
      ])
         .pipe(
            filter(([r, p]) => !!p),
            map(
               ([
                  rows,
                  practices,
                  productType,
                  onlySoldLastTTM,
                  onlyUnconfirmedProducts,
                  onlyUnmappedProducts,
                  onlyActiveProducts,
                  searchText,
                  searchCode,
                  selectedCategory,
                  selectedSite,
                  vcCategories,
               ]) => {
                  const categoryLookup = {}
                  vcCategories.forEach((cat) => (categoryLookup[cat?.vcProductCategorySKey] = cat))

                  this.yourCatList = Array.from(new Set(rows.map((req) => req.categoryName || ''))).sort((a, b) => (a || '').localeCompare(b || ''))

                  this.sites = Array.from(new Set(rows.map((req) => req.siteName || ''))).sort((a, b) => (a || '').localeCompare(b || ''))
                  this.siteCount = this.sites.length ?? 1

                  const filteredRows = rows.filter((row) => {
                     let isValid = true
                     if (productType > 0) isValid = isValid && row.productType === productType

                     if (onlySoldLastTTM) isValid = isValid && !!row.ttmSold

                     if (onlyUnconfirmedProducts) isValid = isValid && (row.mappingScore < 100 || !row.vcProductSKey || row.vcProductSKey == -1)

                     if (onlyUnmappedProducts) isValid = isValid && (!row.vcProductSKey || row.vcProductSKey == -1)

                     /*if (searchText && !!row.name) {
                isValid = (isValid && row.name.toLowerCase().indexOf(searchText.toLowerCase()) > -1);
              }*/

                     if (searchText && (!!row.name || !!row.vcProduct?.name)) {
                        isValid =
                           isValid &&
                           (row.name?.toLowerCase().indexOf(searchText.toLowerCase()) > -1 ||
                              row.vcProduct?.name?.toLowerCase().indexOf(searchText.toLowerCase()) > -1)
                     }

                     if (onlyActiveProducts) {
                        isValid = isValid && !row.notNeeded && !row.isObsolete
                     }

                     if (this.selectedCategory) {
                        isValid = isValid && row.categoryName === this.selectedCategory
                     }

                     if (this.selectedSite) {
                        isValid = isValid && row.siteName === this.selectedSite
                     }

                     if (this.searchCodeInput && !!row.code) {
                        isValid = isValid && row.code.toLowerCase().indexOf(this.searchCodeInput.toLowerCase()) > -1
                     }

                     return isValid
                  })

                  filteredRows.forEach((row) => {
                     row.practice = practices[row.practiceSKey]
                     if (row.vcProduct?.vcProductCategorySKey) row.vcProduct.vcProductCategory = categoryLookup[row.vcProduct?.vcProductCategorySKey]
                  })

                  return filteredRows
               }
            ),
            tap((res) => {
               this.store.patchState({
                  pimsRows: res.sort((a, b) => {
                     if (a.practice?.nameId != b.practice?.nameId) return a.practice?.nameId?.localeCompare(b.practice?.nameId)

                     if (a.productType == ProductType2.Treatment && b.productType != ProductType2.Treatment) return -1
                     else if (a.productType != ProductType2.Treatment && b.productType == ProductType2.Treatment) return 1

                     if (a.categoryName != b.categoryName) return a.categoryName?.localeCompare(b.categoryName)

                     return a.ttmRevenue > b.ttmRevenue ? -1 : 1
                  }),
                  loadingPIMS: false,
               })
            })
         )
         .subscribe()
   }

   onSearch() {
      this.searchTextChanged$.next(this.searchTextInput)
   }

   loadMore($event: LazyLoadEvent) {}

   showMissingPractices() {
      this.missingPracticesVisible = true
   }

   onPracticeSelected(practice: AppPractice) {
      if (!!practice) {
         this.location.go('/products/hospital/' + practice?.id)
      } else {
         this.location.go('/products/hospital')
      }

      this.store.patchState({
         selectedPracticeSKey: practice?.id,
      })
   }

   onProductTypeChanged(product?: ProductTypeEnum) {
      this.store.patchState({
         productTypeSKey: product?.productTypeSKey,
      })
   }

   onCategoryChanged(selectedCategory: string) {
      if (selectedCategory) {
         this.store.setState((state) => ({
            ...state,
            pimsRows: state.pimsRows?.filter((row) => row.categoryName === selectedCategory),
         }))

         this.selectedCategory = selectedCategory
      }

      this.store.patchState({
         selectedCategory: selectedCategory,
      })
   }

   onSiteChanged(site?: string) {
      if (site) {
         this.store.setState((state) => ({
            ...state,
            pimsRows: state.pimsRows?.filter((row) => row.siteName === site),
         }))
         this.selectedSite = site
      }

      this.store.patchState({
         selectedSite: site,
      })
   }

   onSiteClear() {
      this.selectedSite = ''
      this.store.patchState((state) => ({
         ...state,
         selectedSite: this.selectedSite || '',
      }))
   }

   onSearchCode() {
      this.searchCodeChanged$.next(this.searchCodeInput) // called on every input change
   }

   onCategoryClear() {
      this.selectedCategory = '' // or null, depending on your logic
      this.store.patchState((state) => ({
         ...state,
         selectedCategory: this.selectedCategory || '',
      }))
   }

   setOnlyShowSoldTTM($event: CheckboxChangeEvent) {
      this.store.patchState({
         onlySoldLastTTM: $event.checked,
      })
   }

   setOnlyUnconfirmedProducts($event: CheckboxChangeEvent) {
      this.store.patchState({
         onlyUnconfirmedProducts: $event.checked,
      })
   }

   setOnlyUnmappedProducts($event: CheckboxChangeEvent) {
      this.store.patchState({
         onlyUnmappedProducts: $event.checked,
      })
   }

   setOnlyActiveProducts($event: CheckboxChangeEvent) {
      this.store.patchState({
         onlyActiveProducts: $event.checked,
      })
   }
}
